import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AudioLines, Loader2 } from "lucide-react";
import { createRecSession } from "@/lib/recStudioApi";
import { safeApiError } from "@/lib/safeErrorText";
import { decodeSong } from "@/lib/tempoKey";
import { tempoInSong } from "@/lib/loopAlign";

/** The upload's tempo + key, with the beat lined up on this version (null when it wasn't built on the current upload, or can't be found). */
async function songTempo(version, source) {
  if (!source?.tempo || !version.source_uploaded_at || version.source_uploaded_at !== source.uploaded_at) return null;
  try {
    return tempoInSong(source.tempo, await decodeSong(version.audio_url));
  } catch {
    return null;
  }
}

/** Opens a new Recording Studio session with this finished song as the backing — on your loop's beat when it was built on one. */
export default function OpenInStudioButton({ projectId, version, lyrics, source }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(null);
  const open = async () => {
    setBusy(source?.tempo ? "Lining up the beat…" : "Opening…");
    try {
      const tempo = await songTempo(version, source);
      const s = await createRecSession({
        title: `${version.title || "Song"} · my vocal`.slice(0, 120),
        instrumental: { url: version.audio_url, name: version.title || "Song", duration: version.duration || 0, volume: 80, muted: false, solo: false,
          song_id: version.song_id || null, project_id: projectId, ...(tempo ? { tempo } : {}) },
        ...((lyrics || "").trim() ? { lyrics: { text: lyrics.slice(0, 20000), start_sec: 0, end_sec: null, source: "Project lyrics" } } : {}),
      });
      navigate(`/create/studio/${s.id}`);
    } catch (e) {
      toast.error("Couldn't open the Recording Studio", { description: safeApiError(e) });
      setBusy(null);
    }
  };
  return (
    <button type="button" data-testid={`version-studio-${version.id}`} onClick={open} disabled={Boolean(busy)}
      className="inline-flex items-center gap-1.5 rounded-full border border-sky-300/40 bg-sky-500/10 px-4 py-2 font-mono text-[10px] uppercase tracking-wider text-sky-100 transition-colors hover:bg-sky-500/20 disabled:opacity-50">
      {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <AudioLines className="h-3 w-3" />} {busy || "Open in Recording Studio"}
    </button>
  );
}
